import { useEffect, useRef, useState } from 'react';
import './VideoModal.css';

const LOOM_ID = '22a7261f293843edbb9000d1a1c7c8f7';

export default function VideoModal({ open, onClose }) {
  const overlayRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const iframeRef = useRef(null);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Prevent body scroll while open
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // Reset muted state when modal opens
  useEffect(() => {
    if (open) setMuted(true);
  }, [open]);

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  const embedSrc = `https://www.loom.com/embed/${LOOM_ID}?autoplay=1&muted=${muted ? 'true' : 'false'}&hideEmbedTopBar=true&hide_owner=true&hide_title=true&hide_share=true`;

  if (!open) return null;

  return (
    <div
      className="vmodal__overlay"
      ref={overlayRef}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label="Product demo video"
    >
      <div className="vmodal__container">

        {/* Top bar */}
        <div className="vmodal__topbar">
          <div className="vmodal__meta">
            <span className="vmodal__dot" />
            <span className="vmodal__label">Zepliq Setup Walkthrough</span>
          </div>
          <div className="vmodal__controls">
            <button
              className="vmodal__mute-btn"
              onClick={() => {
                setMuted(m => !m);
              }}
              aria-label={muted ? 'Unmute video' : 'Mute video'}
            >
              {muted ? (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                    <line x1="23" y1="9" x2="17" y2="15"/>
                    <line x1="17" y1="9" x2="23" y2="15"/>
                  </svg>
                  Unmute
                </>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/>
                  </svg>
                  Mute
                </>
              )}
            </button>
            <button className="vmodal__close" onClick={onClose} aria-label="Close video">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Video */}
        <div className="vmodal__video-wrap">
          <iframe
            ref={iframeRef}
            key={`${LOOM_ID}-${muted}`}
            src={embedSrc}
            title="Zepliq Setup Video"
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            className="vmodal__iframe"
          />
        </div>

        {/* Bottom hint */}
        <p className="vmodal__hint">Press Esc or click outside to close</p>
      </div>
    </div>
  );
}
